import { supabase } from "@/api/supabaseClient";
import {
  parseCaseTableDefinition,
  type CaseTableCursor,
  type CaseTableDefinition,
} from "@/api/caseTable/caseTableDefinition";

export type CaseTableRow = {
  id: string;
  organization_id: string;
  case_number: string | null;
  name: string | null;
  status: string;
  created_at: string;
  case_year: number;
  case_seq: number;
  care_site_id: string | null;
  care_site_name: string | null;
  found_place_label: string | null;
  bookmarked: boolean;
  can_read_full: boolean;
  current_weight_grams: number | null;
  active_diagnoses: Array<{
    episode_id: string;
    diagnosis_id: string;
    label: string;
    state: string;
  }>;
  current_place_name: string | null;
  thumb_path: string | null;
  species_id?: string | null;
  species_name?: string | null;
  species_unassigned?: boolean;
};

export type CaseTablePage = {
  rows: CaseTableRow[];
  has_more: boolean;
  next_cursor: CaseTableCursor | null;
  filter: CaseTableDefinition;
};

export type SavedCaseListView = {
  id: string;
  name: string;
  definition: CaseTableDefinition;
  updated_at: string;
};

export class CaseTableApi {
  static async list(input: Record<string, unknown>): Promise<CaseTablePage> {
    const definition = parseCaseTableDefinition(input);
    const { data, error } = await supabase.rpc("list_case_table", {
      p_filter: {
        ...definition,
        cursor: input.cursor ?? null,
        limit: input.limit ?? 50,
      },
    });
    if (error) throw error;
    const page = data as CaseTablePage;
    return {
      rows: page.rows ?? [],
      has_more: !!page.has_more,
      next_cursor: page.next_cursor ?? null,
      filter: parseCaseTableDefinition(page.filter ?? definition),
    };
  }

  static async toggleBookmark(caseId: string): Promise<{ case_id: string; bookmarked: boolean }> {
    const { data, error } = await supabase.rpc("toggle_case_bookmark", {
      p_case_id: caseId,
    });
    if (error) throw error;
    return data as { case_id: string; bookmarked: boolean };
  }

  static async isBookmarked(caseId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from("case_bookmarks")
      .select("case_id")
      .eq("case_id", caseId)
      .maybeSingle();
    if (error) throw error;
    return !!data;
  }

  static async listSavedViews(): Promise<SavedCaseListView[]> {
    const { data, error } = await supabase.rpc("list_saved_case_list_views");
    if (error) throw error;
    return ((data ?? []) as SavedCaseListView[]).map((row) => ({
      ...row,
      definition: parseCaseTableDefinition(row.definition),
    }));
  }

  static async upsertSavedView(input: {
    id?: string | null;
    name: string;
    definition: CaseTableDefinition;
  }): Promise<SavedCaseListView> {
    const definition = parseCaseTableDefinition(input.definition);
    const { data, error } = await supabase.rpc("upsert_saved_case_list_view", {
      p_id: input.id ?? null,
      p_name: input.name,
      p_definition: definition,
    });
    if (error) throw error;
    const row = data as SavedCaseListView;
    return { ...row, definition: parseCaseTableDefinition(row.definition) };
  }

  static async deleteSavedView(id: string): Promise<void> {
    const { error } = await supabase.rpc("delete_saved_case_list_view", {
      p_id: id,
    });
    if (error) throw error;
  }
}
